// src/hooks/useAuth.js
import { useState, useEffect, useCallback } from 'react';

export const useAuth = () => {
  // Load user from localStorage on initialization
  const getInitialUser = () => {
    const id = localStorage.getItem('userId');
    const username = localStorage.getItem('username');
    const activeSubscription = localStorage.getItem('activeSubscription') === 'true';
    const jwtToken = localStorage.getItem('jwtToken');
    const expiration = localStorage.getItem('tokenExpiration');
    const stripeCustomerId = localStorage.getItem('stripeCustomerId');
    const expiryDate = localStorage.getItem('expiryDate');
    const subscriptionStatus = localStorage.getItem('subscriptionStatus');
    const trialEnd = localStorage.getItem('trialEnd');

    if (id && username && jwtToken && (!expiration || new Date().getTime() <= parseInt(expiration))) {
      return { 
        id, 
        username,
        activeSubscription, 
        stripeCustomerId,
        expiryDate,
        subscriptionStatus,
        trialEnd
      };
    }
    return null;
  };

  const [user, setUser] = useState(getInitialUser);
  const [isAuthenticated, setIsAuthenticated] = useState(!!getInitialUser());
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Clear authentication data
  const clearAuth = useCallback(() => {
    localStorage.removeItem('jwtToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('tokenType');
    localStorage.removeItem('userId');
    localStorage.removeItem('username');
    localStorage.removeItem('activeSubscription');
    localStorage.removeItem('tokenExpiration');
    localStorage.removeItem('stripeCustomerId');
    localStorage.removeItem('expiryDate');
    localStorage.removeItem('subscriptionStatus');
    localStorage.removeItem('trialEnd');

    setUser(null);
    setIsAuthenticated(false);
  }, []);

  // Login function
  const login = useCallback((userData) => {
    if (userData.jwtToken) {
      localStorage.setItem('jwtToken', userData.jwtToken);
      localStorage.setItem('refreshToken', userData.refreshToken);
      localStorage.setItem('tokenType', userData.tokenType || 'Bearer');
      localStorage.setItem('userId', userData.id);
      localStorage.setItem('role', userData.role);
      localStorage.setItem('username', userData.username);
      localStorage.setItem('activeSubscription', userData.activeSubscription ? 'true' : 'false');
      
      // Only store in localStorage if provided
      if (userData.stripeCustomerId) {
        localStorage.setItem('stripeCustomerId', userData.stripeCustomerId);
      }
      if (userData.expiryDate) {
        localStorage.setItem('expiryDate', userData.expiryDate);
      }
      if (userData.subscriptionStatus) {
        localStorage.setItem('subscriptionStatus', userData.subscriptionStatus);
      }
      if (userData.trialEnd) {
        localStorage.setItem('trialEnd', userData.trialEnd);
      }

      const expirationTime = new Date().getTime() + 60 * 60 * 1000; // 1 hour
      localStorage.setItem('tokenExpiration', expirationTime);
    }

    // Always preserve all user data in state, regardless of localStorage
    const newUser = {
      id: userData.id,
      username: userData.username,
      role: userData.role,
      activeSubscription: userData.activeSubscription || false,
      stripeCustomerId: userData.stripeCustomerId || undefined,
      expiryDate: userData.expiryDate || undefined,
      subscriptionStatus: userData.subscriptionStatus,
      trialEnd: userData.trialEnd
    };

    setUser(newUser);
    setIsAuthenticated(true);
  }, []);

  // Logout function - CHANGED: Now redirects to login
  const logout = useCallback(() => {
    clearAuth();
  }, [clearAuth]);

  // Get authorization headers for API calls
  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('jwtToken');
    const tokenType = localStorage.getItem('tokenType') || 'Bearer';
    const headers = { 'Content-Type': 'application/json' };
    if (token && isAuthenticated) headers.Authorization = `${tokenType} ${token}`;
    return headers;
  }, [isAuthenticated]);

  // Refresh token function
  const refreshToken = useCallback(async () => {
    const refreshTokenValue = localStorage.getItem('refreshToken');
    if (!refreshTokenValue || refreshing) return false;

    setRefreshing(true);
    try {
      const res = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: refreshTokenValue }),
      });

      if (!res.ok) throw new Error('Refresh token failed');

      const data = await res.json();
      localStorage.setItem('jwtToken', data.jwtToken);
      const expirationTime = new Date().getTime() + 60 * 60 * 1000; // 1 hour
      localStorage.setItem('tokenExpiration', expirationTime);

      // Update user data if provided in refresh response
      if (data.user) {
        setUser(prevUser => {
          if (!prevUser) return data.user;
          
          // Only update properties that are actually provided in the response
          const updatedUser = { ...prevUser };
          
          if (data.user.id !== undefined) updatedUser.id = data.user.id;
          if (data.user.username !== undefined) updatedUser.username = data.user.username;
          if (data.user.role !== undefined) updatedUser.role = data.user.role;
          if (data.user.activeSubscription !== undefined) {
            updatedUser.activeSubscription = data.user.activeSubscription;
            localStorage.setItem('activeSubscription', data.user.activeSubscription ? 'true' : 'false');
          }
          if (data.user.stripeCustomerId !== undefined) {
            updatedUser.stripeCustomerId = data.user.stripeCustomerId;
            localStorage.setItem('stripeCustomerId', data.user.stripeCustomerId);
          }
          if (data.user.subscriptionStatus !== undefined) {
            updatedUser.subscriptionStatus = data.user.subscriptionStatus;
            localStorage.setItem('subscriptionStatus', data.user.subscriptionStatus);
          }
          if (data.user.trialEnd !== undefined) {
            updatedUser.trialEnd = data.user.trialEnd;
            localStorage.setItem('trialEnd', data.user.trialEnd);
          }
          if (data.user.expiryDate !== undefined) {
            updatedUser.expiryDate = data.user.expiryDate;
            localStorage.setItem('expiryDate', data.user.expiryDate);
          }
          
          return updatedUser;
        });
      } else {
        // If no user data in refresh response, ensure user state matches localStorage
        setUser(prevUser => {
          if (!prevUser) return prevUser;
          
          const id = localStorage.getItem('userId');
          const username = localStorage.getItem('username');
          const role = localStorage.getItem('role');
          const activeSubscription = localStorage.getItem('activeSubscription') === 'true';
          const stripeCustomerId = localStorage.getItem('stripeCustomerId');
          const expiryDate = localStorage.getItem('expiryDate');
          const subscriptionStatus = localStorage.getItem('subscriptionStatus');
          const trialEnd = localStorage.getItem('trialEnd');
          
          return {
            id: id || prevUser.id,
            username: username || prevUser.username,
            role: role || prevUser.role,
            activeSubscription,
            subscriptionStatus: subscriptionStatus || prevUser.subscriptionStatus,
            trialEnd: trialEnd || prevUser.trialEnd,
            stripeCustomerId: stripeCustomerId || prevUser.stripeCustomerId,
            expiryDate: expiryDate || prevUser.expiryDate
          };
        });
      }

      setRefreshing(false);
      return true;
    } catch (err) {
      console.error('Token refresh failed', err);
      logout();
      setRefreshing(false);
      return false;
    }
  }, [logout, refreshing]);

  // Authenticated fetch - CHANGED: Check token expiry and handle 401 without refresh
  const authenticatedFetch = useCallback(
    async (url, options = {}) => {
      // Check if token is expired before making request
      const expiration = localStorage.getItem('tokenExpiration');
      if (expiration && new Date().getTime() > parseInt(expiration)) {
        console.log('Token expired, logging out');
        logout();
        throw new Error('Token expired');
      }

      const headers = getAuthHeaders();
      let config = { ...options, headers: { ...headers, ...options.headers } };

      try {
        let response = await fetch(url, config);

        // If 401, logout immediately without attempting refresh
        if (response.status === 401) {
          console.log('Received 401, logging out');
          logout();
          throw new Error('Unauthorized');
        }

        return response;
      } catch (error) {
        console.error('Authenticated fetch failed:', error);
        throw error;
      }
    },
    [getAuthHeaders, logout]
  );

  // Check authentication & proactively refresh token
  const checkAuth = useCallback(() => {
    const token = localStorage.getItem('jwtToken');
    const expiration = localStorage.getItem('tokenExpiration');
    const id = localStorage.getItem('userId');
    const username = localStorage.getItem('username');
    const role = localStorage.getItem('role');
    const activeSubscription = localStorage.getItem('activeSubscription') === 'true';
    const stripeCustomerId = localStorage.getItem('stripeCustomerId');
    const expiryDate = localStorage.getItem('expiryDate');
    const subscriptionStatus = localStorage.getItem('subscriptionStatus');
    const trialEnd = localStorage.getItem('trialEnd');

    if (token && id && username) {
      const now = new Date().getTime();
      const currentUser = { 
        id, 
        username, 
        role,
        activeSubscription, 
        stripeCustomerId: stripeCustomerId || undefined, // Keep undefined if not in localStorage
        expiryDate: expiryDate || undefined,
        subscriptionStatus,
        trialEnd
      };
      
      // Only update state if user data has actually changed, but preserve existing properties
      setUser(prevUser => {
        if (!prevUser) return currentUser;
        
        // Merge with existing user data, preserving properties that aren't in localStorage
        const mergedUser = {
          id: currentUser.id,
          username: currentUser.username,
          role: currentUser.role,
          activeSubscription: currentUser.activeSubscription,
          subscriptionStatus: currentUser.subscriptionStatus,
          trialEnd: currentUser.trialEnd,
          stripeCustomerId: currentUser.stripeCustomerId || prevUser.stripeCustomerId,
          expiryDate: currentUser.expiryDate || prevUser.expiryDate
        };
        
        // Only update if something actually changed
        if (prevUser.id !== mergedUser.id || 
            prevUser.username !== mergedUser.username ||
            prevUser.role !== mergedUser.role ||
            prevUser.activeSubscription !== mergedUser.activeSubscription ||
            prevUser.subscriptionStatus !== mergedUser.subscriptionStatus ||
            prevUser.trialEnd !== mergedUser.trialEnd ||
            prevUser.stripeCustomerId !== mergedUser.stripeCustomerId ||
            prevUser.expiryDate !== mergedUser.expiryDate) {
          return mergedUser;
        }
        return prevUser;
      });
      
      setIsAuthenticated(prevAuth => prevAuth ? prevAuth : true);
      
      // // Refresh 2 minutes before expiration (but don't call from checkAuth to avoid loops)
      // if (expiration && now > parseInt(expiration) - 2 * 60 * 1000 && !refreshing) {
      //   setTimeout(() => refreshToken(), 0); // Defer to next tick
      // }
      
      return true;
    }

    clearAuth();
    return false;
  }, [clearAuth, refreshToken, refreshing]);

  // Update user subscription status
  const updateUserSubscription = useCallback((subscriptionData) => {
    setUser(prevUser => {
      if (!prevUser) return prevUser;
      
      const updatedUser = {
        ...prevUser,
        activeSubscription: subscriptionData.activeSubscription !== undefined ? subscriptionData.activeSubscription : prevUser.activeSubscription,
        stripeCustomerId: subscriptionData.stripeCustomerId || prevUser.stripeCustomerId,
        subscriptionStatus: subscriptionData.subscriptionStatus || prevUser.subscriptionStatus,
        trialEnd: subscriptionData.trialEnd || prevUser.trialEnd,
        expiryDate: subscriptionData.expiryDate || prevUser.expiryDate
      };
      
      // Update localStorage for values we want to persist
      localStorage.setItem('activeSubscription', updatedUser.activeSubscription ? 'true' : 'false');
      if (updatedUser.stripeCustomerId) {
        localStorage.setItem('stripeCustomerId', updatedUser.stripeCustomerId);
      }
      if (updatedUser.expiryDate) {
        localStorage.setItem('expiryDate', updatedUser.expiryDate);
      }
      if (updatedUser.subscriptionStatus) {
        localStorage.setItem('subscriptionStatus', updatedUser.subscriptionStatus);
      }
      if (updatedUser.trialEnd) {
        localStorage.setItem('trialEnd', updatedUser.trialEnd);
      }
      
      return updatedUser;
    });
  }, []);

  // Initial check on mount
  useEffect(() => {
    const token = localStorage.getItem('jwtToken');
    const id = localStorage.getItem('userId');
    const username = localStorage.getItem('username');
    const role = localStorage.getItem('role');
    const activeSubscription = localStorage.getItem('activeSubscription') === 'true';
    const stripeCustomerId = localStorage.getItem('stripeCustomerId');
    const expiryDate = localStorage.getItem('expiryDate');
    const subscriptionStatus = localStorage.getItem('subscriptionStatus');
    const trialEnd = localStorage.getItem('trialEnd');

    if (token && id && username) {
      // Only set properties that exist, preserve any existing user data
      setUser(prevUser => {
        const newUser = {
          id, 
          username, 
          role,
          activeSubscription,
          subscriptionStatus,
          trialEnd,
          stripeCustomerId: stripeCustomerId || (prevUser ? prevUser.stripeCustomerId : undefined),
          expiryDate: expiryDate || (prevUser ? prevUser.expiryDate : undefined)
        };
        return newUser;
      });
      setIsAuthenticated(true);
    } else {
      setUser(null);
      setIsAuthenticated(false);
    }
    
    setLoading(false);
  }, []); // Empty dependency array to run only once

  // CHANGED: Check for token expiry every minute and logout if expired
  useEffect(() => {
    const interval = setInterval(() => {
      
      if (isAuthenticated) {
        console.log('Checking token expiration...');
        const expiration = localStorage.getItem('tokenExpiration');
        console.log('Token expiration time:', expiration);
        if (expiration) {
          const now = new Date().getTime();
          const expirationTime = parseInt(expiration);
          
          // If token is expired, logout immediately
          console.log('Current time:', now, 'Expiration time:', expirationTime);
          if (now > expirationTime) {
            console.log('Token expired, logging out');
            logout();
          }
        }
      }
    }, 3600000);

    return () => clearInterval(interval);
  }, [isAuthenticated, logout]);

  return {
    user,
    isAuthenticated,
    loading,
    login,
    logout,
    checkAuth,
    getAuthHeaders,
    authenticatedFetch,
    updateUserSubscription,
    refreshing
  };
};